"use client";

import type { Transition } from "motion/react";
import { motion, useAnimation } from "motion/react";
import type { HTMLAttributes } from "react";
import React, { forwardRef, useCallback, useImperativeHandle } from "react";

import { cn } from "@/lib/utils";
import { useHoverResetGuard } from "./useHoverResetGuard";

export interface LayersIconHandle {
  startAnimation: () => void;
  stopAnimation: () => void;
}

interface LayersIconProps extends HTMLAttributes<HTMLDivElement> {
  size?: number;
  /** e.g. `inherit` from sidebar route row so `currentColor` follows the nav icon color */
  color?: string;
}

const DEFAULT_TRANSITION: Transition = {
  type: "spring",
  stiffness: 100,
  damping: 14,
  mass: 1,
};

const pathVariants = {
  normal: { y: 0 },
  firstState: { y: -9 },
  secondState: { y: 0 },
};

const pathVariantsMid = {
  normal: { y: 0 },
  firstState: { y: -5 },
  secondState: { y: 0 },
};

const LayersIcon = forwardRef<LayersIconHandle, LayersIconProps>(
  (
    {
      onMouseEnter,
      onMouseLeave,
      className,
      size = 20,
      color,
      style,
      ...props
    },
    ref
  ) => {
    const controls = useAnimation();
    const { withLockedHoverReset, releaseHoverResetLock, shouldApplyHoverReset } = useHoverResetGuard();

    useImperativeHandle(
      ref,
      () => ({
        startAnimation: () =>
          void withLockedHoverReset(async () => {
            await controls.start("firstState");
            await controls.start("secondState");
          }),
        stopAnimation: () => {
          releaseHoverResetLock();
          void controls.start("normal");
        },
      }),
      [controls, releaseHoverResetLock, withLockedHoverReset]
    );

    const handleMouseEnter = useCallback(
      async (e: React.MouseEvent<HTMLDivElement>) => {
        onMouseEnter?.(e);
        await controls.start("firstState");
        await controls.start("secondState");
      },
      [controls, onMouseEnter]
    );

    const handleMouseLeave = useCallback(
      (e: React.MouseEvent<HTMLDivElement>) => {
        onMouseLeave?.(e);
        if (!shouldApplyHoverReset()) return;
        void controls.start("normal");
      },
      [controls, onMouseLeave, shouldApplyHoverReset]
    );

    return (
      <div
        className={cn(className)}
        style={{ color: color ?? undefined, ...style }}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        {...props}
      >
        <svg
          fill="none"
          height={size}
          stroke="currentColor"
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth="2"
          viewBox="0 0 24 24"
          width={size}
          xmlns="http://www.w3.org/2000/svg"
        >
          <path d="m12.83 2.18a2 2 0 0 0-1.66 0L2.6 6.08a1 1 0 0 0 0 1.83l8.58 3.91a2 2 0 0 0 1.66 0l8.58-3.9a1 1 0 0 0 0-1.83Z" />
          <motion.path
            animate={controls}
            d="m22 17.65-9.17 4.16a2 2 0 0 1-1.66 0L2 17.65"
            initial={{ y: 0 }}
            transition={DEFAULT_TRANSITION}
            variants={pathVariants}
          />
          <motion.path
            animate={controls}
            d="m22 12.65-9.17 4.16a2 2 0 0 1-1.66 0L2 12.65"
            initial={{ y: 0 }}
            transition={DEFAULT_TRANSITION}
            variants={pathVariantsMid}
          />
        </svg>
      </div>
    );
  }
);

LayersIcon.displayName = "LayersIcon";

export { LayersIcon };
